import { useEffect, useState } from 'react';
import { MapPin, Gauge, Clock, AlertTriangle, FileText, Pencil, Loader2, Image as ImageIcon, Map as MapIcon, BellRing, Timer } from 'lucide-react';
import { api, useAuthStore } from '../../lib/auth';
import { AdminEditDialog } from './AdminEditDialog';
import { ResetButton } from './ResetButton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { STATE, fmtLong, dur, mapsUrl, answersOf, filesOf, loc } from './helpers';
import { StartStopRouteMap } from './RouteMap';
import { DistanceBreakdown } from './DistanceBreakdown';
import { WorkTimeline } from './WorkTimeline';
import { useTranslation, useI18nStore } from '../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);


const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{title}</h3>
    {children}
  </section>
);

function PhaseCard({ r, ph, onEdit }) {
  const p = r[ph];
  const isStart = ph === 'start';
  const tone = isStart ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-rose-200 bg-rose-50/50 dark:bg-rose-950/20';
  if (!p) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-400 flex flex-col items-center justify-center gap-2" data-testid={`ss-detail-${ph}-empty`}>
        {isStart ? tt('noStart') : tt('noStop')}
        {!isStart && r.start && onEdit && (
          <button type="button" onClick={() => onEdit('stop')} className="rounded-lg bg-rose-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-rose-700" data-testid="ss-admin-complete-stop">{tt('completeStop')}</button>
        )}
      </div>
    );
  }
  const sent = new Date(p.submitted_at).toLocaleTimeString(loc(), { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' });
  const list = answersOf(r, ph);
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${tone}`} data-testid={`ss-detail-${ph}`}>
      <div className="flex items-baseline justify-between">
        <span className={`text-[10px] font-black uppercase tracking-widest ${isStart ? 'text-emerald-700' : 'text-rose-700'}`}>{isStart ? 'Start' : 'Stop'}</span>
        <span className="flex items-center gap-2">
          {onEdit && <button type="button" onClick={() => onEdit(ph)} className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-white" title={tt('edit')} data-testid={`ss-admin-edit-${ph}`}><Pencil className="h-3.5 w-3.5" /></button>}
          <span className="text-2xl font-black tabular-nums">{p.entered_time}</span>
          {!isStart && r.start?.entered_time && p.entered_time < r.start.entered_time && <span className="text-[10px] font-bold text-indigo-600" data-testid="ss-detail-stop-nextday">{tt('nextDay')}</span>}
        </span>
      </div>
      {(p.time_preset || p.location_source === 'depot') && (
        <div className="flex flex-wrap gap-1 text-[10px] font-bold" data-testid={`ss-detail-${ph}-preset`}>
          {p.time_preset && <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">{tt('presetTimeBadge', { time: p.client_time || sent })}</span>}
          {p.location_source === 'depot' && <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">{tt('presetLocBadge')}</span>}
        </div>
      )}
      {(p.completed_by_admin || p.edited_by_admin) && <div className="text-[10px] font-bold text-violet-700">{p.completed_by_admin ? tt('byAdminCompleted') : tt('byAdminEdited')}</div>}
      {p.time_changed && <div className="text-[11px] text-amber-700 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{tt('sentAt', { time: sent, diff: `${p.time_diff_min > 0 ? '+' : ''}${p.time_diff_min}` })}</div>}
      <div className="flex items-center gap-1.5 text-sm"><Gauge className="h-4 w-4 text-slate-400" /><b className="tabular-nums">{p.km ?? (p.km_photo ? 'foto' : '—')}</b><span className="text-xs text-slate-500">km</span></div>
      {p.lat != null && (
        <a href={mapsUrl(p)} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 text-xs text-sky-700 hover:underline">
          <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{p.address || `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`}{p.accuracy ? ` (±${p.accuracy} m)` : ''}</span>
        </a>
      )}
      {p.location_source === 'depot' && (p.device
        ? <a href={mapsUrl(p.device)} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 text-[11px] text-slate-500 hover:underline" data-testid={`ss-detail-${ph}-device`}>
            <MapPin className="h-3 w-3 shrink-0 mt-0.5" /><span>{tt('deviceLoc')}: {p.device.address || `${p.device.lat.toFixed(5)}, ${p.device.lng.toFixed(5)}`}{p.device.accuracy ? ` (±${p.device.accuracy} m)` : ''}</span>
          </a>
        : <div className="text-[11px] text-slate-400" data-testid={`ss-detail-${ph}-device-none`}>{tt('deviceLocNone')}</div>)}
      {list.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1 border-t border-black/5 text-xs">
          {list.map(([l, v]) => <div key={l} className="min-w-0"><dt className="text-[10px] text-slate-500 truncate">{l}</dt><dd className="font-semibold truncate">{String(v)}</dd></div>)}
        </dl>
      )}
    </div>
  );
}

function MapSection({ r }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/planning/assignments/${r.assignment_id}/start-stop/route-map`).then((res) => setData(res.data?.data)).catch((e) => setErr(e?.response?.data?.detail || tt('mapFailed')));
  }, [r.assignment_id, r.start?.submitted_at, r.stop?.submitted_at, r.start?.km, r.stop?.km, r.start?.lat, r.start?.lng, r.stop?.lat, r.stop?.lng, r.corrections?.length]);
  if (err) return <p className="text-xs text-rose-600">{err}</p>;
  if (!data) return <div className="h-72 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse flex items-center justify-center text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin mr-1" />{tt('computing')}</div>;
  return <div className="space-y-3"><WorkTimeline w={data.work} r={r} /><StartStopRouteMap data={data} /><DistanceBreakdown data={data} /></div>;
}

const NOTIF = { start_prompt: ['nfStart', 'bg-emerald-100 text-emerald-700'], close: ['nfClose', 'bg-rose-100 text-rose-700'], overdue: ['nfOverdue', 'bg-amber-100 text-amber-700'] };

function Notifications({ r }) {
  const list = r.notifications || [];
  if (!list.length) return <p className="text-xs text-slate-400" data-testid="ss-notif-empty">{tt('noNotifs')}</p>;
  return (
    <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-1.5 space-y-2" data-testid="ss-notif-list">
      {list.map((n, i) => {
        const [k, cls] = NOTIF[n.type] || NOTIF.overdue;
        return (
          <li key={`${n.at}-${i}`} className="pl-4 relative text-xs" data-testid={`ss-notif-${i}`}>
            <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-slate-400 ring-2 ring-white dark:ring-slate-900" />
            <span className="font-bold tabular-nums mr-2">{new Date(n.at).toLocaleString(loc(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' })}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>{tt(k, { m: n.min })}</span>
          </li>
        );
      })}
    </ol>
  );
}

function Corrections({ r }) {
  if (!r.corrections.length) return <p className="text-xs text-slate-400">{tt('noCorrections')}</p>;
  return r.corrections.map((c) => (
    <div key={c.at} className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs space-y-0.5" data-testid={`ss-correction-${r.assignment_id}`}>
      <p className="font-semibold">{new Date(c.at).toLocaleString(loc())} · {c.by_name}{c.admin && <span className="ml-2 rounded bg-violet-100 text-violet-700 px-1 text-[10px]">{c.completed ? tt('adminStopCompleted') : `Admin · ${c.phase === 'start' ? 'Start' : 'Stop'}`}</span>}</p>
      {Object.keys(c.after).map((k) => {
        const ck = k.startsWith('answers.') ? k.slice(8) : null;
        const st = c.phase === 'start';
        const name = ck ? ((r.custom_fields || []).find((f) => f.key === ck)?.label || ck)
          : ({ km: tt(st ? 'startKm' : 'endKm'), entered_time: tt(st ? 'startTime' : 'endTime'), lat: tt('lat'), lng: tt('lng'), address: tt('address') }[k] || k);
        return <p key={k}>{name}: <s className="text-slate-400">{String(c.before[k] ?? '—')}</s> → <b>{String(c.after[k])}</b></p>;
      })}
      <p className="italic">{tt('reason')}: {c.reason}</p>
    </div>
  ));
}

export function RecordDetailSheet({ r, onClose, onReset, resetting, onChanged }) {
  useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = ['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN'].includes(role);
  const [editPhase, setEditPhase] = useState(null);
  const files = r ? filesOf(r) : [];
  const [lbl, cls] = r ? STATE[r.state] : [];
  return (
    <Sheet open={!!r} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="ss-detail-sheet">
        {r && (
          <>
            <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-100 dark:border-slate-800 text-left space-y-1">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>{((d) => d.charAt(0).toUpperCase() + d.slice(1))(fmtLong(r.date))}</span><span>·</span><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{lbl}</span>
                {r.correction_used && <span className="rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-bold">{tt('corrected')}</span>}
                {r.reminders?.length > 0 && <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-bold" title={r.reminders.map((x) => new Date(x).toLocaleString(loc())).join(', ')} data-testid="ss-detail-reminders">{tt('nReminders', { n: r.reminders.length })}</span>}
              </div>
              <SheetTitle className="text-xl font-black">{r.employee_name}</SheetTitle>
              <p className="text-sm text-slate-500"><b className="text-slate-800 dark:text-slate-200">{r.route_code || '—'}</b>{r.route_name ? ` · ${r.route_name}` : ''} · {r.depot_name || '—'}</p>
              <div className="flex items-center gap-4 pt-2 text-sm">
                <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-slate-400" />{dur(r.duration_min)}</span>
                {r.work?.work_total_min != null && <span className="flex items-center gap-1 font-bold" data-testid="ss-detail-work-total"><Timer className="h-4 w-4 text-slate-400" />{tt('workShort', { d: dur(r.work.work_total_min) })}</span>}
                <span className="flex items-center gap-1"><Gauge className="h-4 w-4 text-slate-400" />{r.km_total != null ? tt('kmOdo', { km: r.km_total }) : '—'}</span>
                {(r.state === 'started' || r.state === 'stopped') && <ResetButton r={r} onConfirm={onReset} busy={resetting} />}
              </div>
            </SheetHeader>
            <div className="p-5 space-y-6">
              <div className="grid sm:grid-cols-2 gap-3"><PhaseCard r={r} ph="start" onEdit={canEdit ? setEditPhase : null} /><PhaseCard r={r} ph="stop" onEdit={canEdit ? setEditPhase : null} /></div>
              {r.window && r.state === 'started' && r.window.state === 'closed' && (
                <div className="rounded-lg bg-rose-50 text-rose-800 text-xs p-2.5" data-testid="ss-detail-window-closed">{tt('windowClosed', { at: r.window.closes_at.slice(0, 16).replace('T', ' ') })}</div>
              )}
              {editPhase && <AdminEditDialog r={r} phase={editPhase} onClose={() => setEditPhase(null)} onSaved={() => { setEditPhase(null); onChanged(); }} />}
              {r.start && <Section icon={MapIcon} title={tt('secRoute')} testId="ss-detail-map-section"><MapSection r={r} /></Section>}
              <Section icon={ImageIcon} title={tt('secFiles', { n: files.length })}>
                <div className="flex flex-wrap gap-2">
                  {files.length ? files.map((f) => (
                    <a key={f.id} href={f.cdn_url} target="_blank" rel="noreferrer" className="h-20 w-20 rounded-lg border overflow-hidden bg-slate-100 flex items-center justify-center" title={f.label || f.file_name}>
                      {(f.content_type || '').startsWith('image/') ? <img src={f.cdn_url} alt="" className="h-full w-full object-cover" /> : <FileText className="h-6 w-6 text-slate-400" />}
                    </a>
                  )) : <span className="text-xs text-slate-400">{tt('none')}</span>}
                </div>
              </Section>
              <Section icon={BellRing} title={tt('secNotifs', { n: (r.notifications || []).length })} testId="ss-detail-notifs"><Notifications r={r} /></Section>
              <Section icon={Pencil} title={tt('secCorrections')}><Corrections r={r} /></Section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
